// web-v2/src/services/session-token.service.js
//
// One deal's client session token, asked for only when a surface needs it (personal-logins, PLAN R1
// "/pipeline"): /pipeline list items no longer carry every client's token. The door is
// GET /api/tracon/pipeline/:dealId/session-token -> { success, session_token | null } (broker gate,
// each read logged). A deal with no session ({ ok:true, token:null }) is an answer; a failed read
// ({ ok:false }) is not, and the caller says so. Relative imports for tests/pipeline-board-token-fetch.

import { api } from '../lib/api.js';
import { inFlightDedup } from '../lib/inFlight.js';

export const sessionTokenPath = (dealId) => `/api/tracon/pipeline/${encodeURIComponent(dealId)}/session-token`;

// The door answers from the pipeline cache and can wait on a cold buildPipeline after a restart
// (review L5-UX-9), so the read gets the long timeout; an API older than the door answers the
// server.js catch-all 404 'Not found', which is expected (quiet) and is a failure the caller says.
export const SESSION_TOKEN_TIMEOUT_MS = 90_000;
export const SLOW_NOTICE_MS = 1200;
export const TOKEN_FAILED_WORDS = 'The client link could not be loaded, so only the deal templates are offered.';
export const TOKEN_SLOW_WORDS = 'Loading the client link for Compose...';
const DOOR_MISSING = Object.freeze({ status: 404, code: 'Not found' });

export async function getDealSessionToken(dealId) {
  if (dealId == null || dealId === '') return { ok: false, token: null };
  try {
    const d = await inFlightDedup(`session-token:${dealId}`, () => api.get(sessionTokenPath(dealId), { timeoutMs: SESSION_TOKEN_TIMEOUT_MS, expected: [DOOR_MISSING] }));
    if (!d || d.success !== true || !('session_token' in d)) return { ok: false, token: null };
    if (d.session_token === null) return { ok: true, token: null };
    return typeof d.session_token === 'string' && d.session_token ? { ok: true, token: d.session_token } : { ok: false, token: null };
  } catch {
    return { ok: false, token: null };
  }
}

// The token for one pipeline item (review L5-UX-4). A list item from an API older than L4's /pipeline
// change still carries session_token (the key present, null for no session): that IS the old API's
// answer, so it is used and the door that API lacks is never asked. A new list item has no such key
// and the door answers. When L4's list is live this branch is inert. `onSlow` shows a pending notice
// once the fetch outlasts SLOW_NOTICE_MS and returns how to remove it (L5-UX-9).
export async function tokenForItem({ item = null, dealId, onSlow = null, slowMs = SLOW_NOTICE_MS } = {}) {
  if (item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'session_token')) {
    const own = item.session_token;
    return typeof own === 'string' && own ? { ok: true, token: own } : { ok: true, token: null };
  }
  let hide = null;
  const timer = typeof onSlow === 'function' ? setTimeout(() => { hide = onSlow(); }, slowMs) : null;
  try {
    return await getDealSessionToken(dealId != null ? dealId : item && item.deal_id);
  } finally {
    if (timer) clearTimeout(timer);
    if (typeof hide === 'function') { try { hide(); } catch { /* notice already gone */ } }
  }
}

/** onSlow for a sonner toast: a loading toast, dismissed when the fetch settles. */
export const toastNotice = (t) => () => { const id = t.loading(TOKEN_SLOW_WORDS); return () => t.dismiss(id); };
